import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../../../styles/theme';
import { Box, styled, IconButton, Divider, TextField } from '@mui/material';
import { ReactComponent as EntradaLogo } from '../../../assets/image/EntradaSide.svg';
import { ReactComponent as Schild } from '../../../assets/image/Schild.svg';
import { dimensions } from '../../../styles/dimensions';
import ChristmasOverlaySVG from '../../../assets/image/SpringOverlay.svg';
import LanguageSelectorLogin from '../../../components/LanguageSelectorLogin';
import { useTranslation } from 'react-i18next';
import LogoEntradaWithShield from '../../../assets/image/LogoEntrada_Diap+schildje.png';

type Props = {
  children: JSX.Element;
};

const { common } = theme.palette;
const { base5XL, baseSM } = dimensions;

const LogoContainer = styled(Box)`
  position: absolute;
  left: 50%;
  top: calc(50% - 360px);
  transform: translateX(-50%);
  z-index: 4;
  width: 441px;
  height: 89px;
  margin-top: -60px;
  opacity: 1;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const SmallLogo = styled(Box)`
  width: 47px;
  height: 53px;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const BackgroundOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
`;

const BackgroundImage = styled('img')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 1;
`;

const Sun = styled('div')`
  position: absolute;
  top: 40px;
  right: 40px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, #ffd700, #ffa500, #ff8c00);
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.8), inset 0 0 20px rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.5s ease;
  z-index: 6;

  // Rays using ::before
  &:before {
    content: '';
    position: absolute;
    top: -30px;
    left: -30px;
    right: -30px;
    bottom: -30px;
    background: repeating-conic-gradient(
      from 0deg,
      transparent 0deg,
      transparent 18deg,
      rgba(255, 215, 0, 0.5) 20deg,
      transparent 22deg,
      transparent 38deg
    );
    border-radius: 50%;
    animation: rotate 10s linear infinite;
  }

  // Glow using ::after
  &:after {
    content: '';
    position: absolute;
    top: -15px;
    left: -15px;
    right: -15px;
    bottom: -15px;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.4) 0%, rgba(255, 215, 0, 0) 70%);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }

  &:hover {
    transform: scale(1.2) rotate(360deg);
    box-shadow: 0 0 50px rgba(255, 215, 0, 0.9), inset 0 0 30px rgba(255, 255, 255, 0.6);

    &:before {
      animation: rotate 5s linear infinite; // Faster rotation on hover
    }

    &:after {
      animation: pulse 1s ease-in-out infinite; // Faster pulse on hover
    }
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.9;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
`;

const Cloud = styled('div')<{ $delay: number; $duration: number; $top: number }>`
  position: absolute;
  width: 100px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  top: ${props => props.$top}%;
  left: -120px;
  animation: float ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  z-index: 3;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.1));

  &:before,
  &:after {
    content: '';
    position: absolute;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
  }

  &:before {
    width: 50px;
    height: 50px;
    top: -20px;
    left: 15px;
  }

  &:after {
    width: 40px;
    height: 40px;
    top: -15px;
    left: 45px;
  }

  @keyframes float {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(calc(100vw + 120px));
    }
  }
`;

export const MainLayout: React.FunctionComponent<Props> = ({ children }) => {
  const [showChristmas] = useState(true);
  const { t } = useTranslation();
  const [clouds, setClouds] = useState<
    Array<{
      id: number;
      delay: number;
      duration: number;
      top: number;
    }>
  >([]);

  useEffect(() => {
    // Start with just one cloud
    setClouds([{ id: 1, delay: 0, duration: 30, top: 15 }]);

    // Add new cloud every 30 seconds
    const interval = setInterval(() => {
      const newCloud = {
        id: Date.now(),
        delay: 0,
        duration: Math.random() * 15 + 30, // 30-45 seconds
        top: Math.random() * 30 + 5 // 5-35%
      };
      setClouds(prev => [...prev.slice(-1), newCloud]); // Keep max 2 clouds
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Wrapper>
      <Sun />
      {showChristmas && (
        <BackgroundOverlay>
          <BackgroundImage src={ChristmasOverlaySVG} alt="Christmas overlay" />
        </BackgroundOverlay>
      )}
      {clouds.map(cloud => (
        <Cloud key={cloud.id} $delay={cloud.delay} $duration={cloud.duration} $top={cloud.top} />
      ))}
      <LogoContainer>
        <img src={LogoEntradaWithShield} alt="Entrada Security Systems" />
      </LogoContainer>
      <ContentContainer>{children}</ContentContainer>
      <BottomBar>
        <LanguageSelectorLogin />
        <ContactLink href="https://entradasecurity.com/contact/" target="_blank" rel="noopener noreferrer">
          Contact
        </ContactLink>
      </BottomBar>
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(203, 240, 101);
  position: relative;
  overflow: hidden;
`;

const BackgroundLogo = styled(EntradaLogo)`
  position: absolute;
  width: 90vh;
  height: 90vh;
  opacity: 0.15;
  fill: rgb(177, 218, 67);
  pointer-events: none;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const ContentContainer = styled(Box)`
  background: ${common.white};
  border-radius: 8px;
  padding: ${base5XL}px;
  padding-top: ${Number(base5XL) * 1.6}px;
  padding-bottom: ${Number(base5XL) * 1.3}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  width: 460px;
  height: 450px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: -10px;
`;

const BottomBar = styled(Box)`
  position: absolute;
  top: calc(50% + 225px + 5px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: center;
  z-index: 4;
`;

const ContactLink = styled('a')`
  color: ${common.white};
  text-decoration: underline;
  font-size: 17px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;
